// StatusPill — the canonical ticket-status chip.
//
// Translated from docs/design_handoff_helpdesk/prototype/primitives.jsx
// `StatusPill`, following EXAMPLE_StatusPill.tsx. Every number below traces to
// that prototype; tokens are read as CSS custom properties, never hard-coded.
//
// The colour NEVER comes from settings. `label` may be overridden by
// DeskSettings.statusLabels; the token key always resolves from the canonical
// TicketStatus, so a partial settings object cannot produce an uncoloured chip.
//
// Presentational only — do not add onClick. In the ticket header this pill is
// WRAPPED by the status control (a bordered button opening the
// allowedTransitions menu); in the queue it is inert.
import React from 'react';
import { Box } from '@mui/material';
import { TicketStatus } from '../types';

const STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'New',
  open: 'Open',
  pending: 'Pending',
  resolved: 'Resolved',
  closed: 'Closed'
};

interface StatusPillProps {
  status: TicketStatus;
  /** DeskSettings.statusLabels[status], when the desk defines one. Overrides
   *  the WORD only — never the colour. */
  label?: string;
  /** SubstatusDef.label, rendered inside the same pill so it never becomes a
   *  competing second chip. */
  substatus?: string | null;
  size?: 'sm' | 'md';
}

export default function StatusPill({ status, label, substatus, size = 'md' }: StatusPillProps) {
  const fontSize = size === 'sm' ? 11.5 : 12.5;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: size === 'sm' ? '2px 8px' : '3px 9px',
        borderRadius: 999,
        backgroundColor: `var(--tk-${status}-bg)`,
        color: `var(--tk-${status}-fg)`,
        fontSize,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '3px',
          backgroundColor: `var(--tk-${status}-dot)`,
          flexShrink: 0
        }}
      />
      {label || STATUS_LABEL[status]}
      {substatus ? (
        <Box component="span" sx={{ fontWeight: 500, opacity: 0.72, fontSize: fontSize - 1 }}>
          {`· ${substatus}`}
        </Box>
      ) : null}
    </Box>
  );
}
